import { useEffect, useMemo, useRef, useState } from 'react';
import { FunnelInsightsApiClient } from '../api/insightsClient';
import { describeInsightsError, type InsightsErrorCopy } from '../api/errorCopy';
import type {
  ExperimentFeasibilityPlan,
  FeasibilityPlanOverrides,
  FeasibilityPlanScope,
} from '../types';

/** Debounce so scope/metric/override typing doesn't hammer the planner. */
const DEBOUNCE_MS = 500;

interface UseFeasibilityPlanInput {
  projectId: string;
  enabled: boolean;
  scope?: FeasibilityPlanScope;
  overrides?: FeasibilityPlanOverrides;
}

interface UseFeasibilityPlanResult {
  plan: ExperimentFeasibilityPlan | null;
  loading: boolean;
  error: string | null;
  /** Dictionary-mapped copy for `error` (headline + collapsible techDetails). */
  errorCopy: InsightsErrorCopy | null;
  /** Advisory retry — refetches with the current inputs. */
  retry: () => void;
}

/**
 * J1 — advisory MDE/feasibility plan for the hypothesis form. Debounced on
 * scope/override changes, abortable, never throws into the form: errors land
 * in `error` and the form stays submittable.
 */
export function useFeasibilityPlan(input: UseFeasibilityPlanInput): UseFeasibilityPlanResult {
  const [plan, setPlan] = useState<ExperimentFeasibilityPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCopy, setErrorCopy] = useState<InsightsErrorCopy | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const client = useMemo(() => new FunnelInsightsApiClient(), []);

  // Serialize the request inputs so the effect re-runs on VALUE changes only
  // (callers may rebuild scope/override objects every render).
  const requestKey = JSON.stringify({
    scope: input.scope ?? null,
    overrides: input.overrides ?? null,
  });
  const latestInput = useRef(input);
  latestInput.current = input;

  useEffect(() => {
    if (!input.enabled || !input.projectId) {
      setPlan(null);
      setLoading(false);
      setError(null);
      setErrorCopy(null);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setErrorCopy(null);

    const timer = setTimeout(() => {
      client.planExperimentMde({
        projectId: latestInput.current.projectId,
        scope: latestInput.current.scope,
        overrides: latestInput.current.overrides,
        signal: controller.signal,
      })
        .then(result => {
          setPlan(result);
          setLoading(false);
        })
        .catch(err => {
          if (controller.signal.aborted) return;
          // Shared dictionary (B2): raw BQ leaks map to operator copy, plain
          // errors pass through with the legacy message.
          const copy = err instanceof Error
            ? describeInsightsError(err)
            : { headline: 'Failed to load the feasibility estimate', severity: 'error' as const };
          setError(copy.headline);
          setErrorCopy(copy);
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, input.enabled, input.projectId, requestKey, retryKey]);

  return {
    plan,
    loading,
    error,
    errorCopy,
    retry: () => setRetryKey(key => key + 1),
  };
}
